import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { PopoverContent, PopoverControlled, PopoverTrigger } from '@1trading/react/components/ui/popover';
import { PseudoInputButton, PseudoInputIcon, PseudoInputLabel, PseudoInputOptions } from '@1trading/react/components/ui/pseudo-input';
import { ChevronsUpDown } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { InstitutionBranchOperationsOrDefaults } from '@/features/institution/branch-operations/schema';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';

const InstitutionBranchOperationsFieldsetJurisdictionLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.branchOperations.countryOfBranchOperations.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.branchOperations.countryOfBranchOperations.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionBranchOperationsFieldsetJurisdictionLegend.displayName = 'InstitutionBranchOperationsFieldsetJurisdictionLegend';

const InstitutionBranchOperationsFieldsetJurisdiction = () => {
  const form = useFormContext<InstitutionBranchOperationsOrDefaults>();
  const { defaultValues } = form.formState;
  const { countriesAsOptions } = useMainstreamCountries();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="countryOfBranchOperations"
      render={({ field }) => (
        <FormItem className="col-span-2">
          <FormLabel>{t('institutionDetails.branchOperations.countryOfBranchOperations.title')}*</FormLabel>
          <PopoverControlled>
            {(_, setOpen) => (
              <>
                <PopoverTrigger asChild>
                  <FormControl>
                    <PseudoInputButton role="combobox" value={field.value} {...(!field.value && { 'data-placeholder': '' })}>
                      <PseudoInputLabel>
                        {field.value
                          ? countriesAsOptions.find((option) => option.value === field.value)?.label
                          : t('institutionDetails.branchOperations.countryOfBranchOperations.placeholders.countryOfBranchOperations')}
                      </PseudoInputLabel>
                      <PseudoInputIcon>
                        <ChevronsUpDown />
                      </PseudoInputIcon>
                    </PseudoInputButton>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="p-0" stretch>
                  <PseudoInputOptions
                    empty={t('statuses.noResults')}
                    options={countriesAsOptions}
                    placeholder={t('fundamentals.searchCountries')}
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(field.value === value ? defaultValues?.countryOfBranchOperations : value);
                      setOpen(false);
                    }}
                  />
                </PopoverContent>
              </>
            )}
          </PopoverControlled>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
InstitutionBranchOperationsFieldsetJurisdiction.displayName = 'InstitutionBranchOperationsFieldsetJurisdiction';

export { InstitutionBranchOperationsFieldsetJurisdiction, InstitutionBranchOperationsFieldsetJurisdictionLegend };
